import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { KANT } from "@/lib/kantstrasse";
import { Video } from "lucide-react";

/*
  AKT I — REALISIERT · Kantstraße
  Nur echte Fotos vom Standort. Label: FOTO · PILOT KANTSTRASSE 2026.
  Videos (kantstrasse-loop-16-9.mp4 / kantstrasse-uebergang-loop.mp4)
  werden eingesetzt, sobald die Dateien vorliegen: src unten eintragen.
*/

const M = "/media/realisiert";

const VIDEO_MAIN = {
  sources: [
    { src: `${M}/kantstrasse-loop-16-9.webm`, type: "video/webm" },
    { src: `${M}/kantstrasse-loop-16-9.mp4`, type: "video/mp4" },
  ],
  poster: `${M}/uebergang-barrierefrei.jpg`,
};
const VIDEO_UEBERGANG = {
  sources: [
    { src: `${M}/kantstrasse-uebergang-loop.webm`, type: "video/webm" },
    { src: `${M}/kantstrasse-uebergang-loop.mp4`, type: "video/mp4" },
  ],
};

const ease = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "", testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 1, delay, ease }}
    className={className}
    data-testid={testid}
  >
    {children}
  </motion.div>
);

const FotoLabel = () => {
  const { lang } = useLang();
  return (
    <div className="absolute top-3 left-3 md:top-4 md:left-4 z-10 inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-white/85 bg-black/50 backdrop-blur px-3 py-1.5 border border-white/10 font-display">
      <span className="h-1 w-1 rounded-full bg-white/70" />
      {lang === "de" ? "Foto · Pilot Kantstraße 2026" : "Photo · Kantstraße Pilot 2026"}
    </div>
  );
};

const Caption = ({ children }) => (
  <p className="mt-3 text-[13px] leading-relaxed text-white/50 max-w-3xl">
    {children}
  </p>
);

const Photo = ({ src, alt, caption, aspect, pos, testid }) => (
  <figure data-testid={testid} className="w-full">
    <div className={`relative w-full ${aspect} overflow-hidden border border-white/10`}>
      <img
        src={src}
        alt={alt || caption}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        style={pos ? { objectPosition: pos } : undefined}
      />
      <FotoLabel />
    </div>
    <Caption>{caption}</Caption>
  </figure>
);

const LoopVideo = ({ sources, poster, aspect, caption, testid }) => (
  <figure data-testid={testid} className="w-full">
    <div className={`relative w-full ${aspect} overflow-hidden border border-white/10 bg-[#16191D]`}>
      {sources && sources.length ? (
        <video
          ref={(el) => { if (el) el.muted = true; }}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={poster}
          onCanPlay={(e) => { const p = e.currentTarget.play(); if (p && p.catch) p.catch(() => {}); }}
        >
          {sources.map((s) => (
            <source key={s.src} src={s.src} type={s.type} />
          ))}
        </video>
      ) : poster ? (
        <>
          <img
            src={poster}
            alt={caption}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <FotoLabel />
          <div className="absolute top-3 right-3 md:top-4 md:right-4 z-10 inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-white/60 bg-black/50 backdrop-blur px-3 py-1.5 border border-white/10 font-display">
            <Video className="h-3 w-3" strokeWidth={1.4} />
            Video folgt
          </div>
        </>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(45deg, transparent 48%, rgba(255,255,255,.7) 49%, rgba(255,255,255,.7) 51%, transparent 52%)",
              backgroundSize: "20px 20px",
            }}
          />
          <Video className="h-6 w-6 text-white/40" strokeWidth={1.2} />
          <div className="text-[10px] tracking-[0.35em] uppercase text-white/50 font-display">
            Video benötigt
          </div>
        </div>
      )}
    </div>
    {caption && <Caption>{caption}</Caption>}
  </figure>
);

export default function Realisiert() {
  const { lang } = useLang();
  const copy = {
    de: {
      kicker: "Akt I — Realisiert",
      claim: "Aus Sicherheitsräumen werden Lebensräume.",
      title: KANT.de.title,
      intro:
        "Zwischen Waldorf Astoria und Upper West steht die erste vollständige Berlin-Cube-Anlage im öffentlichen Straßenraum. Bepflanzt, beleuchtet, begehbar — und geprüfter Zufahrtsschutz. Kein Rendering: Fotos vom Standort.",
      rueckbau: KANT.de.status,
      capVideo: "Kantstraße, Juli 2026 — die vollständige Anlage im Abendlicht.",
      cap01:
        "Sitzen, durchatmen, genießen. Unter Bäumen, im Lavendelduft. Das Schöne ist: Es wird angenommen — von allen.",
      cap02:
        "Blick zur Gedächtniskirche. Bäume, Lavendel und Stauden stehen in den Modulen, nicht daneben.",
      cap03:
        "Sonnenblumen mitten in der Kantstraße. Die Module tragen Pflanzen, Bänke und den geprüften Schutz zugleich.",
      cap04:
        "Die Lichtnut läuft rundum. Nachts markiert sie die Anlage, ohne den Straßenraum auszuleuchten.",
      cap05:
        "Substrattiefe für Gehölze statt Wechselflor. Die Bepflanzung ist auf Standzeit ausgelegt.",
      cap06: "Sitzhöhe und Holzauflage sind Teil des Moduls, nicht angebaut.",
      uebergangTitle: "Der Übergang",
      uebergangText:
        "Zwischen den Modulen liegen befahrbare Rampen. Der Radweg läuft durch, der Gehweg bleibt erreichbar. Und wo Rettungswege frei bleiben müssen, werden die Module versetzt gesetzt — geschützt und trotzdem durchfahrbar. Zufahrtsschutz heißt nicht Absperrung.",
      cap07:
        "Bezirksbürgermeisterin Kirstin Bauch, AG-City-Vorstand Patrick Wittke und Martin Fichtelmann an der Anlage, Juni 2026.",
      alt01: "Menschen sitzen am Abend auf den Bänken der Berlin-Cube-Anlage in der Kantstraße in Berlin",
      alt02: "Berlin-Cube-Module in der Kantstraße in Berlin mit Blick zur Gedächtniskirche",
      alt03: "Sonnenblumen in einem bepflanzten Berlin-Cube-Modul in der Kantstraße in Berlin",
      alt04: "Berlin-Cube-Anlage in der Kantstraße in Berlin zur blauen Stunde mit umlaufender Lichtnut",
      alt05: "Nahaufnahme der Bepflanzung eines Berlin-Cube-Moduls in der Kantstraße in Berlin",
      alt06: "Sitzbank mit Holzauflage an einem Berlin-Cube-Modul in der Kantstraße in Berlin",
      alt07: "Bezirksbürgermeisterin Kirstin Bauch, Patrick Wittke und Martin Fichtelmann an der Berlin-Cube-Anlage in der Kantstraße in Berlin",
      footnote:
        "Pilotaufbau in Kooperation mit der AG City Berlin und dem Bezirksamt Charlottenburg-Wilmersdorf. Aufbauzeit der gesamten Anlage: rund zehn Stunden.",
    },
    en: {
      kicker: "Act I — Built",
      claim: "Spaces built for security become spaces built for life.",
      title: KANT.en.title,
      intro:
        "Between the Waldorf Astoria and Upper West, the first complete Berlin-Cube installation stands in public street space. Planted, lit, walkable — and a crash-tested vehicle security barrier. Not a rendering: photographs taken on site.",
      rueckbau: KANT.en.status,
      capVideo: "Kantstraße, July 2026 — the complete installation in evening light.",
      cap01:
        "Sit down, breathe, enjoy. Under trees, in the scent of lavender. And the best part: people actually use it — everyone does.",
      cap02:
        "Looking towards the Kaiser Wilhelm Memorial Church. Trees, lavender and perennials grow inside the modules, not beside them.",
      cap03:
        "Sunflowers in the middle of Kantstraße. The modules carry plants, benches and the tested protection at the same time.",
      cap04:
        "The recessed light strip runs right around each module. At night it marks the installation without floodlighting the street.",
      cap05:
        "Substrate deep enough for woody plants, not seasonal bedding. The planting is designed to stay.",
      cap06: "Seat height and timber top are part of the module itself, not an add-on.",
      uebergangTitle: "Getting Through",
      uebergangText:
        "Drivable ramps sit between the modules. The bike lane runs through, the pavement stays accessible. And where emergency routes must remain clear, the modules are placed in a staggered layout — protected and still passable. Access protection does not mean barricading.",
      cap07:
        "District Mayor Kirstin Bauch, AG City Berlin board member Patrick Wittke and Martin Fichtelmann at the installation, June 2026.",
      alt01: "People sitting on the benches of the Berlin-Cube installation on Kantstraße, Berlin, in the evening",
      alt02: "Berlin-Cube modules on Kantstraße, Berlin, looking towards the Kaiser Wilhelm Memorial Church",
      alt03: "Sunflowers in a planted Berlin-Cube module on Kantstraße, Berlin",
      alt04: "Berlin-Cube installation on Kantstraße, Berlin, at dusk with the recessed light strip",
      alt05: "Close-up of the planting of a Berlin-Cube module on Kantstraße, Berlin",
      alt06: "Bench with timber top on a Berlin-Cube module on Kantstraße, Berlin",
      alt07: "District Mayor Kirstin Bauch, Patrick Wittke and Martin Fichtelmann at the Berlin-Cube installation on Kantstraße, Berlin",
      footnote:
        "Pilot installation in cooperation with AG City Berlin and the Charlottenburg-Wilmersdorf District Office. Installation time for the complete run: around ten hours.",
    },
  }[lang];

  return (
    <section
      id="realisiert"
      data-testid="section-realisiert"
      className="relative bg-[#0F1114] text-white py-32 md:py-48 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-14">
        {/* Kopf */}
        <div className="mb-16 md:mb-24 max-w-4xl">
          <Reveal>
            <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-white/45 mb-8 font-display">
              <span className="h-px w-8 bg-white/40" />
              {copy.kicker}
            </div>
            <h2
              className="font-display font-light tracking-[-0.03em] leading-[0.98] text-white"
              style={{ fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)" }}
              data-testid="realisiert-claim"
            >
              {copy.claim}
            </h2>
            <h3
              className="mt-6 md:mt-8 font-display font-light tracking-[-0.02em] leading-[1.1] text-[#F5D77A]"
              style={{ fontSize: "clamp(1.35rem, 2.4vw, 2.1rem)" }}
              data-testid="realisiert-title"
            >
              {copy.title}
            </h3>
            <p
              className="mt-8 md:mt-10 text-white/70 font-display font-light tracking-[-0.005em] leading-[1.4] max-w-3xl"
              style={{ fontSize: "clamp(1.05rem, 1.5vw, 1.45rem)" }}
              data-testid="realisiert-intro"
            >
              {copy.intro}
            </p>
            <p
              className="mt-6 text-white/60 leading-[1.6] max-w-3xl text-base md:text-[17px]"
              data-testid="realisiert-rueckbau"
            >
              {copy.rueckbau}
            </p>
          </Reveal>
        </div>

        <div className="space-y-14 md:space-y-20">
          {/* 0 · Video-Aufmacher */}
          <Reveal testid="realisiert-video-main">
            <LoopVideo
              sources={VIDEO_MAIN.sources}
              poster={VIDEO_MAIN.poster}
              aspect="aspect-[4/3] md:aspect-[16/9]"
              caption={copy.capVideo}
            />
          </Reveal>

          {/* 1 · Aufmacher-Foto */}
          <Reveal testid="realisiert-img-01">
            <Photo
              src={`${M}/01-abend-menschen.jpg`}
              alt={copy.alt01}
              caption={copy.cap01}
              aspect="aspect-[4/5] md:aspect-[16/9]"
              pos="center 72%"
            />
          </Reveal>

          {/* 2 · Raster */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-5">
            <Reveal testid="realisiert-img-02">
              <Photo
                src={`${M}/09-kantstrasse-gedaechtniskirche.jpg`}
                alt={copy.alt02}
                caption={copy.cap02}
                aspect="aspect-[4/3]"
              />
            </Reveal>
            <Reveal delay={0.08} testid="realisiert-img-03">
              <Photo
                src={`${M}/08-sonnenblumen.jpg`}
                alt={copy.alt03}
                caption={copy.cap03}
                aspect="aspect-[4/3]"
              />
            </Reveal>
          </div>

          {/* 3 · Volle Breite */}
          <Reveal testid="realisiert-img-04">
            <Photo
              src={`${M}/04-blaue-stunde.jpg`}
              alt={copy.alt04}
              caption={copy.cap04}
              aspect="aspect-[4/5] md:aspect-[16/9]"
              pos="center 62%"
            />
          </Reveal>

          {/* 4 · Raster */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-5">
            <Reveal testid="realisiert-img-05">
              <Photo
                src={`${M}/05-bepflanzung-nah.jpg`}
                alt={copy.alt05}
                caption={copy.cap05}
                aspect="aspect-[4/5]"
              />
            </Reveal>
            <Reveal delay={0.08} testid="realisiert-img-06">
              <Photo
                src={`${M}/06-bank-sonne.jpg`}
                alt={copy.alt06}
                caption={copy.cap06}
                aspect="aspect-[4/5]"
              />
            </Reveal>
          </div>

          {/* 4b · Video + Text: Der Übergang */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <Reveal testid="realisiert-video-uebergang">
              <LoopVideo sources={VIDEO_UEBERGANG.sources} aspect="aspect-[4/3]" />
            </Reveal>
            <Reveal delay={0.1} testid="realisiert-uebergang-text">
              <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-white/45 mb-6 font-display">
                <span className="h-px w-8 bg-white/40" />
                {copy.uebergangTitle}
              </div>
              <p
                className="text-white/75 font-display font-light tracking-[-0.005em] leading-[1.5]"
                style={{ fontSize: "clamp(1.1rem, 1.6vw, 1.5rem)" }}
              >
                {copy.uebergangText}
              </p>
            </Reveal>
          </div>

          {/* 5 · Abschluss */}
          <Reveal testid="realisiert-img-07">
            <Photo
              src={`${M}/07-bezirk.jpg`}
              alt={copy.alt07}
              caption={copy.cap07}
              aspect="aspect-[4/3] md:aspect-[16/9]"
              pos="center 42%"
            />
          </Reveal>
        </div>

        {/* Abschlusszeile */}
        <Reveal testid="realisiert-footnote">
          <p className="mt-14 text-[12px] text-white/40 italic max-w-2xl leading-relaxed border-l-2 border-white/20 pl-5">
            {copy.footnote}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
